import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supportService } from '../services/support/SupportService';
import { DashboardQueryKey } from '../constants/dashboardQueryKeys';
import type {
  TicketCategory,
  TicketPriority,
  TicketsQueryFilters,
} from '../types/support';

/**
 * How often the queue re-reads itself.
 *
 * Nothing pushes an escalation to the dashboard: `TicketService.escalate`
 * only logs a warning, so without this an agent is told "a person will reply
 * right here in this chat" while no person ever learns the ticket exists.
 * Polling is the mechanism the design spec names for the queue, and 30s is
 * short enough that a waiting user is not left long.
 */
export const TICKETS_POLL_INTERVAL_MS = 30_000;

export function useTicketsQuery(
  page: number,
  pageSize: number,
  filters: TicketsQueryFilters,
) {
  return useQuery({
    queryKey: [DashboardQueryKey.SUPPORT_TICKETS, page, pageSize, filters],
    queryFn: () => supportService.getTickets(page, pageSize, filters),
    // Overrides the app-wide 5-minute staleTime (main.tsx): a queue that only
    // refreshes when someone hits Refresh is not a queue.
    staleTime: 0,
    refetchInterval: TICKETS_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

export function useTicketQuery(id: string | null) {
  return useQuery({
    queryKey: [DashboardQueryKey.SUPPORT_TICKET, id],
    queryFn: () => supportService.getTicket(id as string),
    enabled: Boolean(id),
  });
}

/** Every mutation refreshes the open ticket and the queue behind it. */
function useTicketMutation<TArgs, TData = unknown>(
  mutationFn: (args: TArgs) => Promise<TData>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DashboardQueryKey.SUPPORT_TICKET],
      });
      queryClient.invalidateQueries({
        queryKey: [DashboardQueryKey.SUPPORT_TICKETS],
      });
    },
  });
}

export function useStartChat() {
  return useTicketMutation(({ userId, text }: { userId: string; text: string }) =>
    supportService.startChat(userId, text),
  );
}

export function useAssignTicket() {
  return useTicketMutation((id: string) => supportService.assignTicket(id));
}

export function useReplyToTicket() {
  return useTicketMutation(({ id, text }: { id: string; text: string }) =>
    supportService.replyToTicket(id, text),
  );
}

export function useResolveTicket() {
  return useTicketMutation((id: string) => supportService.resolveTicket(id));
}

export function useUpdateTicket() {
  return useTicketMutation(
    ({
      id,
      patch,
    }: {
      id: string;
      patch: { subject?: string; category?: TicketCategory; priority?: TicketPriority };
    }) => supportService.updateTicket(id, patch),
  );
}

export function useKbSourcesQuery() {
  return useQuery({
    queryKey: [DashboardQueryKey.SUPPORT_KB_SOURCES],
    queryFn: () => supportService.getKbSources(),
  });
}

export function useCreateManualSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ name, text }: { name: string; text: string }) =>
      supportService.createManualSource(name, text),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [DashboardQueryKey.SUPPORT_KB_SOURCES],
      }),
  });
}

export function useToggleSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      supportService.toggleSource(id, enabled),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [DashboardQueryKey.SUPPORT_KB_SOURCES],
      }),
  });
}

/** Retrieval testing is an explicit action, so it is a mutation, not a query. */
export function useTestRetrieval() {
  return useMutation({
    mutationFn: ({ query }: { query: string }) =>
      supportService.testRetrieval(query),
  });
}
